import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axios';
import { toast } from 'react-toastify';
import { usePermissions } from '../../hooks/usePermissions';
import Pagination from '../../components/Pagination';
import ConfirmationModal from '../../components/ConfirmationModal';
import SortableTableHeader from '../../components/SortableTableHeader';
import { CopyHash } from '../../features/web3/forex/components/CopyHash';
import { bscAddressUrl } from '../../lib/format';
import { X } from 'lucide-react';

const BscAddressLink = ({ address }) => {
  if (!address) return <span className="text-muted-foreground/50">N/A</span>;
  return (
    <a
      href={`https://bscscan.com/address/${address}`}
      target="_blank"
      rel="noopener noreferrer"
      title={address}
      className="text-ginox-green hover:underline break-all"
    >
      {address}
    </a>
  );
};

const BscTxLink = ({ hash }) => {
  if (!hash) return <span className="text-muted-foreground/50">N/A</span>;
  return (
    <a
      href={`https://bscscan.com/tx/${hash}`}
      target="_blank"
      rel="noopener noreferrer"
      title={hash}
      className="text-ginox-green hover:underline break-all"
    >
      {hash}
    </a>
  );
};

const GUSDDepositHistory = () => {
  const { token } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    page: 1,
    pageSize: 10,
    username: '',
    status: '',
    startDate: '',
    endDate: ''
  });
  const [tempFilters, setTempFilters] = useState({
    page: 1,
    pageSize: 10,
    username: '',
    status: '',
    startDate: '',
    endDate: ''
  });
  const [sort, setSort] = useState({ key: '', direction: 'asc' });
  const [sortedData, setSortedData] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRows: 0
  });
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [loadingTransaction, setLoadingTransaction] = useState(false);
  const [loadingApprove, setLoadingApprove] = useState(false);
  const [showApproveConfirmModal, setShowApproveConfirmModal] = useState(false);
  const { can } = usePermissions();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.username) params.username = filters.username;
      if (filters.page) params.page = filters.page;
      if (filters.pageSize) params.pageSize = filters.pageSize;
      if (filters.status !== '') params.status = filters.status;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const response = await axiosInstance.get('/api/admin/deposits/gusd/history', {
        headers: { token },
        params
      });

      if (response.data.success) {
        const newData = response.data.data.data;
        setData(newData);
        setSortedData(newData);
        setPagination({
          currentPage: parseInt(response.data.data.currentPage) || 1,
          totalPages: response.data.data.totalPages || 1,
          totalRows: response.data.data.totalRows || 0
        });
      }
    } catch (error) {
      console.error('Error fetching deposit history:', error);
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!data.length) return;
    const sorted = [...data].sort((a, b) => {
      if (!sort.key) return 0;
      let aValue = a[sort.key];
      let bValue = b[sort.key];
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';
      if (sort.key === 'amount' || sort.key === 'fee' || sort.key === 'payable') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
        return sort.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      if (sort.key === 'status') {
        aValue = aValue ? 1 : 0;
        bValue = bValue ? 1 : 0;
        return sort.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      if (sort.key === 'createdAt' || sort.key === 'updatedAt') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
        return sort.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      return sort.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
    setSortedData(sorted);
  }, [data, sort]);

  const handleTempFilterChange = (key, value) => {
    setTempFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setFilters({ ...tempFilters });
  };

  const handleFilterChange = (key, value) => {
    handleTempFilterChange(key, value);
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
    setTempFilters(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    handleFilterChange('pageSize', newPageSize);
    handleFilterChange('page', 1);
  };

  const clearFilters = () => {
    const defaultFilters = {
      page: 1,
      pageSize: 10,
      username: '',
      status: '',
      startDate: '',
      endDate: ''
    };
    setFilters(defaultFilters);
    setTempFilters(defaultFilters);
  };

  const handleSort = (key) => {
    setSort(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const exportToExcel = async () => {
    try {
      const params = {};
      if (filters.username) params.username = filters.username;
      if (filters.status !== '') params.status = filters.status;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      params.pageSize = 10000;
      params.page = 1;

      const response = await axiosInstance.get('/api/admin/deposits/gusd/history', {
        headers: { token },
        params
      });

      const exportData = response.data.data.data;
      if (exportData.length === 0) {
        alert('No data to export');
        return;
      }

      const headers = [
        'Username', 'Wallet Address', 'Transaction ID', 'Amount', 'Currency',
        'Method', 'Pay/Wallet Address', 'Extras', 'Status', 'Created At'
      ];
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => [
          row.username || '',
          row.walletAddress || '',
          row.tranx_id || '',
          row.amount || '',
          row.currency_code || '',
          row.method || '',
          row.payAddress || '',
          row.extras || '',
          row.status ? 'Completed' : 'Pending' || '',
          new Date(row.createdAt).toLocaleString() || ''
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `gusd_deposit_history_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed. Please try again.');
    }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleString();

  const [originalDepositId, setOriginalDepositId] = useState(null);

  const handleViewTransaction = async (depositId) => {
    setLoadingTransaction(true);
    setShowTransactionModal(true);
    setOriginalDepositId(depositId);
    try {
      const response = await axiosInstance.get(`/api/admin/deposits/gusd/${depositId}`, {
        headers: { token }
      });
      if (response.data.success) {
        setTransactionDetails(response.data.data);
        setSelectedTransaction(response.data.data);
      } else {
        toast.error('Failed to fetch transaction details');
      }
    } catch (error) {
      console.error('Error fetching transaction details:', error);
      toast.error('Error fetching transaction details');
    } finally {
      setLoadingTransaction(false);
    }
  };

  const [depositToApprove, setDepositToApprove] = useState(null);

  const handleApproveDeposit = async (depositId) => {
    setDepositToApprove(depositId);
    setShowApproveConfirmModal(true);
  };

  const confirmApproveDeposit = async () => {
    const depositIdToUse = depositToApprove || originalDepositId;
    if (!depositIdToUse) {
      toast.error('No deposit ID found. Please try again.');
      setLoadingApprove(false);
      return;
    }
    setLoadingApprove(true);
    try {
      const requestBody = { depositId: depositIdToUse };
      const response = await axiosInstance.post('/api/admin/deposits/gusd/approve', requestBody, {
        headers: { token }
      });
      if (response.data.success) {
        toast.success('Deposit approved successfully');
        setShowTransactionModal(false);
        setShowApproveConfirmModal(false);
        setDepositToApprove(null);
        fetchData();
      } else {
        toast.error(response.data.message || 'Failed to approve deposit');
      }
    } catch (error) {
      console.error('Error approving deposit:', error);
      toast.error('Error approving deposit');
    } finally {
      setLoadingApprove(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Finance
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
        GUSD Deposit <span className="text-gradient-ginox">History</span>
      </h1>
      <p className="text-sm text-muted-foreground mb-6">View and manage GUSD deposit transactions</p>

      {/* Filters */}
      <div className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">Username / Trx ID / WalletAddress / Pay Address</label>
            <input
              type="text"
              value={tempFilters.username}
              onChange={(e) => handleTempFilterChange('username', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
              placeholder="Search…"
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            />
          </div>

          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">Status</label>
            <select
              value={tempFilters.status}
              onChange={(e) => handleTempFilterChange('status', e.target.value)}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            >
              <option value="">All Status</option>
              <option value="true">Completed</option>
              <option value="false">Pending</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">Start Date</label>
            <input
              type="date"
              value={tempFilters.startDate}
              onChange={(e) => handleTempFilterChange('startDate', e.target.value)}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            />
          </div>

          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">End Date</label>
            <input
              type="date"
              value={tempFilters.endDate}
              onChange={(e) => handleTempFilterChange('endDate', e.target.value)}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            />
          </div>

          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">Page Size</label>
            <select
              value={tempFilters.pageSize}
              onChange={(e) => handleTempFilterChange('pageSize', parseInt(e.target.value))}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            >
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">Page</label>
            <select
              value={tempFilters.page}
              onChange={(e) => handleTempFilterChange('page', parseInt(e.target.value))}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            >
              {Array.from({ length: pagination.totalPages }, (_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={applyFilters} className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors">
            Apply Filters
          </button>
          <button onClick={clearFilters} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
            Clear Filters
          </button>
          <button type="button" onClick={exportToExcel} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
            Export to Excel
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <p className="py-12 text-center text-sm text-muted-foreground">Loading...</p>
        ) : (
          <>
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr>
                  <SortableTableHeader
                    sortKey="username"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Username
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="tranx_id"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Transaction ID
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="amount"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Amount
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="currency_code"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Currency
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="method"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Method
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="payAddress"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Pay/Wallet Address
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="extras"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Extras
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="status"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Status
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="createdAt"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Created At
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="updatedAt"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Updated At
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortable={false}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Actions
                  </SortableTableHeader>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((item) => (
                  <tr key={item._id} className="group hover:bg-white/[0.02] transition-colors">
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                      <div>{item.username}</div>
                      <div className="text-xs text-muted-foreground/80">
                        <CopyHash value={item.walletAddress} href={bscAddressUrl(item.walletAddress)} head={6} tail={4} />
                      </div>
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{item.tranx_id}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{item.amount}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">{item.currency_code}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">{item.method}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                      <CopyHash value={item.payAddress} href={bscAddressUrl(item.payAddress)} head={6} tail={4} />
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">{item.extras}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">
                      {item.status ? (
                        <span className="inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-400">Completed</span>
                      ) : (
                        <span className="inline-flex items-center rounded-full border border-yellow-500/30 bg-yellow-500/10 px-2 py-0.5 text-[10px] font-medium text-yellow-400">Pending</span>
                      )}
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{formatDate(item.createdAt)}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{formatDate(item.updatedAt)}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">
                      {can('GET:/api/admin/deposits/gusd/:depositId') && (
                        <button
                          onClick={() => handleViewTransaction(item._id)}
                          className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                          View
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

          </>
        )}
      </div>
      <div className="border-t border-white/[0.08] px-4">
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalRows={pagination.totalRows}
          pageSize={filters.pageSize}
          onPageChange={handlePageChange}
        />
      </div>

      {/* Transaction Details Modal */}
      {showTransactionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-xl border border-border/70 bg-card p-6 shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif italic text-lg text-foreground">Transaction Details</h2>
              <button
                onClick={() => {
                  setShowTransactionModal(false);
                  setTransactionDetails(null);
                  setSelectedTransaction(null);
                  setOriginalDepositId(null);
                }}
                className="rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-white/[0.05] transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {loadingTransaction ? (
              <p className="py-12 text-center text-sm text-muted-foreground">Loading...</p>
            ) : transactionDetails ? (
              <div className="space-y-4">
                {transactionDetails.depositId ? (
                  /* Wallet Connect response */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Deposit ID</label>
                      <p className="text-sm text-gray-900 dark:text-white break-all">{transactionDetails.depositId}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Username</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.username || "N/A"}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Transaction ID</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.tranx_id || "N/A"}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Amount</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.amount}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Amount Received</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.amountReceived}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Wallet Type</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.wallet_type || "N/A"}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.status || "N/A"}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Sweep Status</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.sweepStatus || "N/A"}</p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">From Wallet Address</label>
                      <p className="text-sm break-all">
                        <BscAddressLink address={transactionDetails.fromWalletAddress} />
                      </p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">To Wallet Address</label>
                      <p className="text-sm break-all">
                        <BscAddressLink address={transactionDetails.toWalletAddress} />
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Created At</label>
                      <p className="text-sm text-gray-900 dark:text-white">{formatDate(transactionDetails.createdAt)}</p>
                    </div>
                    {transactionDetails.blockchain && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Blockchain Info</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-gray-50 dark:bg-gray-700 rounded-md p-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Current Balance</label>
                            <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.blockchain.currentBalance}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">On-Chain Amount</label>
                            <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.blockchain.onChainAmount}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Transfer Detected</label>
                            <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.blockchain.transferDetected ? "Yes" : "No"}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Tx Status</label>
                            <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.blockchain.txStatus || "N/A"}</p>
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">On-Chain Tx Hash</label>
                            <p className="text-sm break-all">
                              <BscTxLink hash={transactionDetails.blockchain.onChainTxHash} />
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    {transactionDetails.sweepStatus === "pending" && (
                      <div className="md:col-span-2 mt-2">
                        <button
                          onClick={() => navigate("/deposits/gusd-sweep")}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                        >
                          Go to Sweep Page
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  /* NowPayments response */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Payment ID</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.payment_id}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Invoice ID</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.invoice_id || "N/A"}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Payment Status</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.payment_status}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Pay Address</label>
                      <p className="text-sm text-gray-900 dark:text-white break-all">{transactionDetails.pay_address}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Price Amount</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.price_amount}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Price Currency</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.price_currency}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Pay Amount</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.pay_amount}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Actually Paid</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.actually_paid}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Pay Currency</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.pay_currency}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Order ID</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.order_id}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Order Description</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.order_description}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Purchase ID</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.purchase_id}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Outcome Amount</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.outcome_amount}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Outcome Currency</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.outcome_currency}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Payout Hash</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.payout_hash || "N/A"}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Payin Hash</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.payin_hash || "N/A"}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Created At</label>
                      <p className="text-sm text-gray-900 dark:text-white">{formatDate(transactionDetails.created_at)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Updated At</label>
                      <p className="text-sm text-gray-900 dark:text-white">{formatDate(transactionDetails.updated_at)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Burning Percent</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.burning_percent || "N/A"}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Type</label>
                      <p className="text-sm text-gray-900 dark:text-white">{transactionDetails.type}</p>
                    </div>
                  </div>
                )}

                {/* Approve Button */}
                {!transactionDetails.depositId && can("POST:/api/admin/deposits/gusd/approve") && (
                  <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => handleApproveDeposit(originalDepositId)}
                      disabled={loadingApprove}
                      className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50"
                    >
                      {loadingApprove ? 'Approving...' : 'Approve Deposit'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <p className="py-12 text-center text-sm text-muted-foreground">No transaction details available</p>
            )}
          </div>
        </div>
      )}

      {/* Approve Deposit Confirmation Modal */}
      <ConfirmationModal
        isOpen={showApproveConfirmModal}
        onClose={() => {
          setShowApproveConfirmModal(false);
          setDepositToApprove(null);
        }}
        onConfirm={confirmApproveDeposit}
        title="Approve Deposit"
        message={`Are you sure you want to approve the deposit of ${selectedTransaction?.amount} GUSD for user ${selectedTransaction?.username}?`}
        confirmText="Approve"
        loading={loadingApprove}
        loadingText="Approving..."
      />
    </div>
  );
};

export default GUSDDepositHistory;
